import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Icon from '../Icon';
import PlainButton from '../PlainButton';
import { tokens, orange, inkA } from '../tokens';

// Token input (combobox): a row of chips (initials avatar, label, remove)
// followed by a text box, with a suggestion list underneath.
//
// Props
//   values        [{ key, label, detail? }]            the chips
//   suggestions   [{ key, label, detail?, tag? }]      shown while typing
//   onQueryChange(text)   every edit, and on focus (fetch suggestions)
//   onAdd(item)   a suggestion, or { key, label, freeText: true } for typed
//                 text that passes validateFreeText
//   onRemove(key)
//   validateFreeText(text) → boolean   (omit to allow suggestions only)
//   freeTextHint  line under the "Add “…”" option
//   noMatchText   shown when nothing matches the typed text
//   placeholder, label (the input's accessible name), error
//
// Keyboard: ↑/↓ move through the list, Enter / comma / Tab add the
// highlighted option, Backspace in an empty box removes the last chip,
// Esc closes the list (a second Esc reaches the surrounding dialog).
// Imported by path (components/v3/ChipsInput), not through the barrel.

let nextId = 0;

const initialsOf = (text) => {
  const clean = String(text || '').trim();
  if (!clean) return '?';
  const words = clean
    .split('@')[0]
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters =
    words.length > 1 ? words[0][0] + words[1][0] : clean.slice(0, 2);
  return letters.toUpperCase();
};

const Avatar = ({ text, size }) => (
  <span
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: Math.round(size * 0.42),
      fontWeight: 800,
      background: orange(0.18),
      color: orange(1),
    }}
  >
    {initialsOf(text)}
  </span>
);

Avatar.propTypes = {
  text: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired,
};

const Box = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 46px;
  padding: 6px 8px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  transition: border-color 150ms ease, box-shadow 150ms ease;

  &:focus-within {
    border-color: ${(p) => p.$focus};
    box-shadow: 0 0 0 3px ${orange(0.12)};
  }
`;

const TextBox = styled.input`
  flex: 1;
  min-width: 140px;
  border: 0;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  padding: 6px 4px;

  &::placeholder {
    color: ${(p) => p.$placeholder};
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 100%;
  padding: 3px 4px 3px 3px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
`;

const RemoveButton = styled(PlainButton)`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};

  &:hover {
    color: #ed4337;
  }
`;

const Option = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  cursor: pointer;
  background: ${(p) => (p.$active ? orange(0.1) : 'transparent')};
`;

const Tag = ({ children, color, background }) => (
  <span
    style={{
      flexShrink: 0,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
      padding: '4px 7px',
      borderRadius: 5,
      color,
      background,
    }}
  >
    {children}
  </span>
);

Tag.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
};

const OptionBody = ({ option, t, theme, freeTextHint }) => {
  const free = option.freeText;
  const ellipsis = {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  return (
    <>
      {free ? (
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: inkA(theme, 0.08),
            color: t.sub,
          }}
        >
          <Icon name="mail" size={13} color="currentColor" />
        </span>
      ) : (
        <Avatar text={option.label} size={26} />
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...ellipsis, fontSize: 13, fontWeight: 600 }}>
          {free ? `Add “${option.label}”` : option.label}
        </span>
        {(free ? freeTextHint : option.detail) ? (
          <span
            style={{ ...ellipsis, fontSize: 11.5, color: t.sub, marginTop: 1 }}
          >
            {free ? freeTextHint : option.detail}
          </span>
        ) : null}
      </span>
      {option.tag ? (
        <Tag color={t.sub} background={inkA(theme, 0.08)}>
          {option.tag}
        </Tag>
      ) : null}
    </>
  );
};

OptionBody.propTypes = {
  option: PropTypes.object.isRequired,
  t: PropTypes.object.isRequired,
  theme: PropTypes.object,
  freeTextHint: PropTypes.node,
};

OptionBody.defaultProps = { theme: undefined, freeTextHint: null };

const ADD_KEYS = ['Enter', ','];

const ChipsInput = ({
  values,
  suggestions,
  onQueryChange,
  onAdd,
  onRemove,
  validateFreeText,
  freeTextHint,
  noMatchText,
  placeholder,
  label,
  error,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const idRef = useRef(null);
  if (!idRef.current) {
    nextId += 1;
    idRef.current = `v3-chips-${nextId}`;
  }
  const listId = `${idRef.current}-list`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const text = query.trim();
  const free =
    text && validateFreeText && validateFreeText(text)
      ? [{ key: `free:${text.toLowerCase()}`, label: text, freeText: true }]
      : [];
  const options = [...suggestions, ...free];
  const current = Math.min(active, Math.max(options.length - 1, 0));
  const showList = open && options.length > 0;
  const showEmpty = open && !options.length && !!text && !!noMatchText;

  const change = (next) => {
    setQuery(next);
    setActive(0);
    setOpen(true);
    if (onQueryChange) onQueryChange(next);
  };

  const add = (option) => {
    onAdd(option);
    setQuery('');
    setActive(0);
    if (onQueryChange) onQueryChange('');
  };

  const move = (delta) => {
    setOpen(true);
    setActive(
      Math.min(Math.max(current + delta, 0), Math.max(options.length - 1, 0))
    );
  };

  const pick = (e) => {
    const option = options[current];
    if (option) {
      e.preventDefault();
      add(option);
    } else if (e.key !== 'Tab' && text) {
      e.preventDefault();
    }
  };

  const KEYS = {
    ArrowDown: (e) => {
      e.preventDefault();
      move(1);
    },
    ArrowUp: (e) => {
      e.preventDefault();
      move(-1);
    },
    Tab: (e) => {
      if (text) pick(e);
    },
    Backspace: () => {
      if (!query && values.length) onRemove(values[values.length - 1].key);
    },
    Escape: (e) => {
      if (!showList && !showEmpty) return;
      e.preventDefault();
      // Keep the surrounding dialog open: this Esc only closes the list.
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
      setOpen(false);
    },
  };

  const onKeyDown = (e) => {
    if (ADD_KEYS.includes(e.key)) {
      pick(e);
      return;
    }
    if (KEYS[e.key]) KEYS[e.key](e);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Box
        $border={error ? '#ED4337' : t.fieldBorder}
        $bg={inkA(theme, 0.04)}
        $focus={error ? '#ED4337' : t.primary}
      >
        {values.map((v) => (
          <Chip
            key={v.key}
            title={v.detail || v.label}
            $border={t.border}
            $bg={inkA(theme, 0.07)}
          >
            <Avatar text={v.label} size={22} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: t.text,
              }}
            >
              {v.label}
            </span>
            <RemoveButton
              aria-label={`Remove ${v.label}`}
              onClick={() => onRemove(v.key)}
              $bg={inkA(theme, 0.08)}
              $color={t.sub}
            >
              <Icon name="x" size={11} color="currentColor" />
            </RemoveButton>
          </Chip>
        ))}
        <TextBox
          type="text"
          role="combobox"
          aria-label={label}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          aria-activedescendant={showList ? `${listId}-${current}` : undefined}
          value={query}
          placeholder={values.length ? 'Add another…' : placeholder}
          onChange={(e) => change(e.target.value)}
          onFocus={() => {
            setOpen(true);
            if (onQueryChange) onQueryChange(query);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          style={{ color: t.text }}
          $placeholder={t.faint}
        />
      </Box>
      <ul
        id={listId}
        role="listbox"
        aria-label={`${label} suggestions`}
        style={{
          display: showList ? 'block' : 'none',
          position: 'absolute',
          left: 0,
          right: 0,
          top: '100%',
          marginTop: 6,
          padding: 0,
          listStyle: 'none',
          zIndex: 20,
          background: t.card,
          color: t.text,
          border: '1px solid ' + t.border,
          borderRadius: 10,
          boxShadow: t.shadowPop,
          overflow: 'hidden',
        }}
      >
        {options.map((option, i) => (
          <Option
            key={option.key}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === current}
            $active={i === current}
            // Keep focus in the text box while clicking an option.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(i)}
            onClick={() => add(option)}
          >
            <OptionBody
              option={option}
              t={t}
              theme={theme}
              freeTextHint={freeTextHint}
            />
          </Option>
        ))}
      </ul>
      {showEmpty ? (
        <div
          role="status"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 6,
            zIndex: 20,
            padding: '12px 14px',
            fontSize: 12.5,
            lineHeight: 1.45,
            color: t.sub,
            background: t.card,
            border: '1px solid ' + t.border,
            borderRadius: 10,
            boxShadow: t.shadowPop,
          }}
        >
          {noMatchText}
        </div>
      ) : null}
    </div>
  );
};

const itemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  detail: PropTypes.string,
  tag: PropTypes.string,
});

ChipsInput.propTypes = {
  values: PropTypes.arrayOf(itemShape).isRequired,
  suggestions: PropTypes.arrayOf(itemShape),
  onQueryChange: PropTypes.func,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  validateFreeText: PropTypes.func,
  freeTextHint: PropTypes.node,
  noMatchText: PropTypes.node,
  placeholder: PropTypes.string,
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
};

ChipsInput.defaultProps = {
  suggestions: [],
  onQueryChange: undefined,
  validateFreeText: undefined,
  freeTextHint: null,
  noMatchText: null,
  placeholder: '',
  error: false,
};

export default ChipsInput;
